import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, GraduationCap, Briefcase, Award, CheckCircle2, ArrowRight, Globe2, Sparkles, FileText, ExternalLink } from 'lucide-react';
import { CLINIC, IMAGES, DOCTORS, QUALIFICATIONS, EXPERIENCE, EXPERTISE, UK_HIGHLIGHTS } from '../mock';
import { PageHero, SectionTitle, Reveal } from '../components/Shared';

export default function WhoWeAre() {
  return (
    <div>
      <PageHero eyebrow="About Us" title="Meet our expert rheumatology team" subtitle="Dedicated to exceptional care for patients with autoimmune and rheumatic conditions across Delhi-NCR." image={IMAGES.waiting} />

      {/* Intro */}
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={IMAGES.examRoom} alt="Modern clinic" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionTitle eyebrow="Our Clinic" title="Comprehensive rheumatology care in Delhi-NCR" />
            <p className="text-slate-600 leading-relaxed">At Srigyan Rheumatology Clinic, we are dedicated to providing exceptional care for patients with autoimmune diseases and rheumatic conditions. Our state-of-the-art facility offers personalised treatment plans, cutting-edge therapies and compassionate support to help our patients manage their conditions and improve their quality of life.</p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {['Personalised plans', 'Advanced diagnostics', 'Biologic therapy', 'Long-term monitoring'].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 size={17} className="text-teal-600" /> {t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Chief doctor */}
      <section className="mint-section py-20">
        <div className="container-x">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
            <Reveal>
              <div className="sticky top-28">
                <div className="rounded-full p-2 shadow-2xl max-w-md mx-auto" style={{ background: 'linear-gradient(140deg,#ffffff,#ccf5e8)' }}>
                  <img src={IMAGES.drMale} alt={DOCTORS[0].name} className="w-full aspect-square rounded-full object-cover" />
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-6 mt-5 border border-teal-50">
                  <div className="font-display text-xl text-teal-900" style={{ fontWeight: 600 }}>{DOCTORS[0].name}</div>
                  <div className="text-teal-700 text-sm font-medium mt-1">{DOCTORS[0].role}</div>
                  <div className="text-slate-500 text-sm mt-2">{DOCTORS[0].creds}</div>
                  <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm mt-4"><Phone size={15} /> Book Appointment</a>
                </div>
              </div>
            </Reveal>
            <div>
              <SectionTitle eyebrow="Chief Rheumatologist" title="A rare blend of UK & India expertise" />
              <p className="text-slate-600 leading-relaxed mb-4">Dr Puneet Srivastava is a UK-trained consultant rheumatologist with extensive experience in diagnosing and managing complex autoimmune and inflammatory rheumatic diseases. Having trained and practised in both the United Kingdom and India, he offers a unique blend of international expertise and personalised, evidence-based care.</p>
              <p className="text-slate-600 leading-relaxed mb-8">He is widely recognised as one of the few rheumatologists in Noida &amp; Delhi NCR with UK Consultant-level experience, known for a meticulous clinical approach and commitment to excellence.</p>

              <div className="mb-10">
                <h3 className="flex items-center gap-2 text-lg text-teal-900 mb-4" style={{ fontWeight: 600 }}><GraduationCap size={20} className="text-teal-600" /> Academic Qualifications</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {QUALIFICATIONS.map((q) => (
                    <div key={q.title} className="bg-white rounded-xl border border-teal-50 p-4">
                      <div className="font-semibold text-teal-800 text-sm">{q.title}</div>
                      <div className="text-slate-500 text-xs mt-1">{q.detail}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-10">
                <h3 className="flex items-center gap-2 text-lg text-teal-900 mb-4" style={{ fontWeight: 600 }}><Briefcase size={20} className="text-teal-600" /> Clinical Experience</h3>
                <ul className="space-y-2.5">
                  {EXPERIENCE.map((e) => (
                    <li key={e} className="flex gap-3 text-sm text-slate-700"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" /> {e}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="flex items-center gap-2 text-lg text-teal-900 mb-4" style={{ fontWeight: 600 }}><Award size={20} className="text-teal-600" /> Areas of Expertise</h3>
                <div className="flex flex-wrap gap-2">
                  {EXPERTISE.map((x) => (
                    <span key={x} className="bg-teal-50 text-teal-800 text-xs font-medium px-3 py-1.5 rounded-full">{x}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UK Experience & Achievements */}
      <section className="py-20 bg-white">
        <div className="container-x">
          <SectionTitle center eyebrow="UK Experience" title="Leadership & achievements from the NHS" sub="Years of consultant-level practice in the UK shaped a rigorous, service-building and teaching-focused approach to rheumatology care." />

          {UK_HIGHLIGHTS.filter((h) => h.featured).map((h) => (
            <Reveal key={h.title}>
              <div className="card-lift bg-white border border-teal-100 rounded-2xl p-8 mb-6 grid md:grid-cols-[auto_1fr] gap-6 items-start">
                <span className="grid place-items-center w-14 h-14 rounded-2xl text-white shrink-0" style={{ background: 'linear-gradient(135deg,#0f766e,#0b3b3a)' }}><FileText size={26} /></span>
                <div>
                  <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-2">Published Research</span>
                  <h3 className="text-xl text-teal-900 mb-2" style={{ fontWeight: 600 }}>{h.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{h.body}</p>
                  {h.ref && (
                    <a href={h.ref.url} target="_blank" rel="noreferrer" className="inline-flex items-start gap-2 text-teal-700 text-sm font-medium mt-4 hover:text-teal-900 transition-colors">
                      <ExternalLink size={16} className="shrink-0 mt-0.5" /> <span>{h.ref.label}</span>
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          ))}

          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-6">
            {UK_HIGHLIGHTS.filter((h) => !h.featured).map((h, i) => (
              <Reveal key={h.title} delay={(i % 3) * 80}>
                <div className="card-lift h-full bg-white border border-teal-50 rounded-2xl p-7 relative overflow-hidden">
                  <span className="grid place-items-center w-12 h-12 rounded-xl text-white mb-5" style={{ background: 'linear-gradient(135deg,#0f766e,#0b3b3a)' }}><Sparkles size={22} /></span>
                  <h3 className="text-lg text-teal-900 mb-2" style={{ fontWeight: 600 }}>{h.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{h.body}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={160}>
              <div className="h-full rounded-2xl p-7 deep-section text-white flex flex-col justify-center">
                <Globe2 size={26} className="text-teal-200 mb-3" />
                <h3 className="font-display text-xl mb-2" style={{ fontWeight: 600 }}>UK consultant standards, in Delhi NCR</h3>
                <p className="text-teal-100/85 text-sm leading-relaxed">From national audits to specialist combined clinics, this NHS experience now benefits every patient at our clinics.</p>
                <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm mt-5 w-fit"><Phone size={16} /> Book a Consultation</a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Medical Director */}
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <SectionTitle eyebrow="Medical Director" title={DOCTORS[1].name} />
            <div className="text-teal-700 text-sm font-medium -mt-8 mb-6">{DOCTORS[1].creds}</div>
            <p className="text-slate-600 leading-relaxed">{DOCTORS[1].bio}</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {DOCTORS[1].tags.map((t) => <span key={t} className="bg-teal-50 text-teal-800 text-xs font-medium px-3 py-1.5 rounded-full">{t}</span>)}
            </div>
          </div>
          <Reveal className="order-1 lg:order-2">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={IMAGES.drFemale} alt={DOCTORS[1].name} className="w-full h-[440px] object-cover object-top" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="deep-section">
        <div className="container-x py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-white text-2xl md:text-3xl text-balance" style={{ fontWeight: 600 }}>Ready to consult a specialist?</h2>
          <div className="flex gap-3">
            <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"><Phone size={18} /> {CLINIC.phone}</a>
            <Link to="/disorders-we-treat" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors">Disorders <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
